import React, { useMemo, useState } from 'react'
import Cookie from 'js-cookie'
import { Spinner } from '@blueprintjs/core'
import { ThemeProvider } from '@mui/material'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import {
  ChartsProvider,
  SnackbarProvider,
  generateChartsTheme,
  getTheme,
} from '@perses-dev/components'
import {
  DataQueriesProvider,
  dynamicImportPluginLoader,
  PluginRegistry,
  TimeRangeProvider,
  usePlugin,
} from '@perses-dev/plugin-system'
import { DatasourceStoreProvider, Panel, VariableProvider } from '@perses-dev/dashboards'
import * as gaugeChartPlugin from '@perses-dev/gauge-chart-plugin'
import * as heatmapChartPlugin from '@perses-dev/heatmap-chart-plugin'
import * as histogramChartPlugin from '@perses-dev/histogram-chart-plugin'
import * as pieChartPlugin from '@perses-dev/pie-chart-plugin'
import * as prometheusPlugin from '@perses-dev/prometheus-plugin'
import * as statChartPlugin from '@perses-dev/stat-chart-plugin'
import * as tablePlugin from '@perses-dev/table-plugin'
import * as timeSeriesChartPlugin from '@perses-dev/timeseries-chart-plugin'

import apis from 'browser/app/models/apis'
import { PanelKind } from './chart-types'

// Perses' own types live in transitive packages; derive them from the providers we import directly.
type TimeRangeProps = React.ComponentProps<typeof TimeRangeProvider>
type TimeRangeValue = TimeRangeProps['timeRange']
type DurationString = NonNullable<TimeRangeProps['refreshInterval']>
type DatasourceStoreProps = React.ComponentProps<typeof DatasourceStoreProvider>
type QueryDefinitions = React.ComponentProps<typeof DataQueriesProvider>['definitions']

// Perses' Prometheus client appends `/api/v1/query_range` etc., so point it at the shim prefix.
const SHIM_BASE = `${apis.getHost()}/1.0/entities/actions/metrics/prometheus`

/**
 * A single default GlobalDatasource in Prometheus direct mode, pointed at the shim. Perses' own fetch
 * does not carry our Bearer token; its Prometheus client sends `proxy.spec.headers` on every request
 * even in direct mode (the URL still comes from `directUrl`), so the token rides there.
 */
function createDatasourceApi(
  authorization: string | undefined
): DatasourceStoreProps['datasourceApi'] {
  const datasource = {
    kind: 'GlobalDatasource' as const,
    metadata: { name: 'VectorMetricsShim' },
    spec: {
      default: true,
      plugin: {
        kind: 'PrometheusDatasource',
        spec: {
          directUrl: SHIM_BASE,
          proxy: {
            kind: 'HTTPProxy',
            spec: {
              url: SHIM_BASE,
              headers: authorization ? { Authorization: authorization } : {},
            },
          },
        },
      },
    },
  }
  return {
    getDatasource: () => Promise.resolve(undefined),
    // Must return the resource itself: the store reads `.spec` and `.metadata.name`.
    getGlobalDatasource: () => Promise.resolve(datasource),
    listDatasources: () => Promise.resolve([]),
    listGlobalDatasources: () => Promise.resolve([datasource]),
    buildProxyUrl: () => SHIM_BASE,
  }
}

// Every panel plugin behind a CHART_TYPES entry must be registered here.
const pluginLoader = dynamicImportPluginLoader(
  [
    prometheusPlugin,
    timeSeriesChartPlugin,
    statChartPlugin,
    gaugeChartPlugin,
    pieChartPlugin,
    tablePlugin,
    histogramChartPlugin,
    heatmapChartPlugin,
  ].map((plugin) => ({
    resource: plugin.getPluginModule(),
    importPlugin: () => Promise.resolve(plugin),
  }))
)

export interface IPersesPanelHostProps {
  panelKind: PanelKind
  title?: string
  queries: string[]
  legendFormat?: string
  /** Panel plugin spec; defaults to the plugin's own initial options. */
  panelSpec?: Record<string, unknown>
  /** Relative window, e.g. '5d' / '1h'; must already be a valid Perses duration. */
  pastDuration?: string
  /** Perses refresh interval, e.g. '5m'; '0s' disables. */
  refreshInterval: string
  height: number
}

interface IChartPanelProps {
  panelKind: PanelKind
  title?: string
  panelSpec?: Record<string, unknown>
}

/** Renders the panel, falling back to the plugin's initial options when the node gives no spec. */
function ChartPanel({ panelKind, title, panelSpec }: IChartPanelProps): JSX.Element {
  const { data: plugin } = usePlugin('Panel', panelKind)
  const spec = panelSpec ?? plugin?.createInitialOptions()
  if (!spec) {
    return <Spinner size={20} />
  }
  return (
    <Panel
      panelOptions={{ hideHeader: !title }}
      definition={{
        kind: 'Panel',
        spec: { display: { name: title ?? 'Metric' }, plugin: { kind: panelKind, spec } },
      }}
    />
  )
}

/** Provider stack (MUI, ECharts, react-query, Perses runtime) around a single Perses panel. */
export default function PersesPanelHost(props: IPersesPanelHostProps): JSX.Element {
  const { panelKind, title, queries, legendFormat, panelSpec, height } = props
  const pastDuration = (props.pastDuration ?? '1d') as DurationString

  const muiTheme = useMemo(() => getTheme('light'), [])
  const chartsTheme = useMemo(() => generateChartsTheme(muiTheme, {}), [muiTheme])
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: { queries: { refetchOnWindowFocus: false, retry: false } },
      }),
    []
  )
  const datasourceApi = useMemo(() => createDatasourceApi(Cookie.get('Authorization')), [])

  const [timeRange, setTimeRange] = useState<TimeRangeValue>({ pastDuration })
  const [refreshInterval, setRefreshInterval] = useState<DurationString>(
    props.refreshInterval as DurationString
  )

  // DataQueriesProvider only runs definitions whose outer kind is 'TimeSeriesQuery'; a flat
  // { kind: 'PrometheusTimeSeriesQuery' } is silently dropped.
  const definitions: QueryDefinitions = useMemo(
    () =>
      queries.map((query) => ({
        kind: 'TimeSeriesQuery',
        spec: {
          plugin: {
            kind: 'PrometheusTimeSeriesQuery',
            spec: legendFormat ? { query, seriesNameFormat: legendFormat } : { query },
          },
        },
      })),
    [queries, legendFormat]
  )

  const dashboardResource = useMemo(
    () =>
      ({
        kind: 'Dashboard',
        metadata: { name: 'embedded', project: 'vector' },
        spec: { datasources: {}, duration: pastDuration, panels: {}, layouts: [], variables: [] },
      }) as unknown as DatasourceStoreProps['dashboardResource'],
    [pastDuration]
  )

  return (
    <ThemeProvider theme={muiTheme}>
      <ChartsProvider chartsTheme={chartsTheme}>
        <SnackbarProvider
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          variant="default"
          content=""
        >
          <QueryClientProvider client={queryClient}>
            <PluginRegistry
              pluginLoader={pluginLoader}
              defaultPluginKinds={{
                Panel: panelKind,
                TimeSeriesQuery: 'PrometheusTimeSeriesQuery',
              }}
            >
              <TimeRangeProvider
                timeRange={timeRange}
                refreshInterval={refreshInterval}
                setTimeRange={setTimeRange}
                setRefreshInterval={setRefreshInterval}
              >
                <VariableProvider>
                  <DatasourceStoreProvider
                    dashboardResource={dashboardResource}
                    datasourceApi={datasourceApi}
                  >
                    <DataQueriesProvider definitions={definitions}>
                      <div style={{ height, width: '100%' }}>
                        <ChartPanel panelKind={panelKind} title={title} panelSpec={panelSpec} />
                      </div>
                    </DataQueriesProvider>
                  </DatasourceStoreProvider>
                </VariableProvider>
              </TimeRangeProvider>
            </PluginRegistry>
          </QueryClientProvider>
        </SnackbarProvider>
      </ChartsProvider>
    </ThemeProvider>
  )
}
